// Google Analytics 4 Integration
// Initialize with your GA4 Measurement ID
// Also logs analytics to Firebase Firestore for admin dashboard

import {
  logPageView as logFirestorePageView,
  logProjectView as logFirestoreProjectView,
  logProjectClick as logFirestoreProjectClick,
  logContactSubmit as logFirestoreContactSubmit,
  logSocialClick as logFirestoreSocialClick,
  logBlogRead as logFirestoreBlogRead,
  logResumeDownload as logFirestoreResumeDownload,
} from "../services/analytics";

interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

interface PageView {
  page_title: string;
  page_location: string;
  page_path: string;
}

// Type declaration for gtag
// The second argument to gtag() is 'string | Date' because the GA4 initialization
// pattern calls window.gtag('js', new Date()) with a Date object, not a string.
declare global {
  interface Window {
    gtag?: (
      _command: string,
      _targetId: string | Date,
      _config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";

export const initGA = (): void => {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) {
    console.warn("Google Analytics not initialized: Missing measurement ID");
    return;
  }

  // Load GA4 script
  // Note: SRI (Subresource Integrity) is not used here because:
  // 1. GA script includes dynamic query parameters that change content
  // 2. Google frequently updates the script, making hash maintenance impractical
  // 3. CSP restricts script-src to trusted domains (www.googletagmanager.com)
  // 4. HTTPS ensures transport layer security
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.crossOrigin = "anonymous"; // Enable CORS for better error reporting
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(..._args) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually
  });

  console.log("Google Analytics initialized");
};

export const trackPageView = (pageData: PageView): void => {
  // Log to Google Analytics
  if (window.gtag) {
    window.gtag("event", "page_view", {
      page_title: pageData.page_title,
      page_location: pageData.page_location,
      page_path: pageData.page_path,
    });
  }

  // Log to Firebase Firestore for admin dashboard
  logFirestorePageView(pageData.page_path, pageData.page_title).catch((error) => {
    console.error("[Analytics] Failed to log page view to Firestore:", error);
  });
};

export const trackEvent = (event: AnalyticsEvent): void => {
  if (!window.gtag) return;

  window.gtag("event", event.action, {
    event_category: event.category,
    event_label: event.label,
    value: event.value,
  });
};

// Convenience functions for common events
export const trackProjectView = (projectName: string): void => {
  trackEvent({
    action: "view_project",
    category: "Projects",
    label: projectName,
  });

  // Log to Firestore
  logFirestoreProjectView(projectName).catch((error) => {
    console.error("[Analytics] Failed to log project view to Firestore:", error);
  });
};

export const trackProjectClick = (projectName: string, linkType: "live" | "github"): void => {
  trackEvent({
    action: "click_project_link",
    category: "Projects",
    label: `${projectName} - ${linkType}`,
  });

  // Log to Firestore
  logFirestoreProjectClick(projectName, linkType).catch((error) => {
    console.error("[Analytics] Failed to log project click to Firestore:", error);
  });
};

export const trackResumeDownload = (): void => {
  trackEvent({
    action: "download_resume",
    category: "Resume",
    label: "PDF Download",
  });

  // Log to Firestore
  logFirestoreResumeDownload().catch((error) => {
    console.error("[Analytics] Failed to log resume download to Firestore:", error);
  });
};

export const trackContactFormSubmit = (success: boolean): void => {
  trackEvent({
    action: "submit_contact_form",
    category: "Contact",
    label: success ? "Success" : "Error",
  });

  // Log to Firestore (only count successful submissions)
  if (success) {
    logFirestoreContactSubmit("contact").catch((error) => {
      console.error("[Analytics] Failed to log contact submit to Firestore:", error);
    });
  }
};

export const trackSocialClick = (platform: string): void => {
  trackEvent({
    action: "click_social_link",
    category: "Social",
    label: platform,
  });

  // Log to Firestore
  logFirestoreSocialClick(platform).catch((error) => {
    console.error("[Analytics] Failed to log social click to Firestore:", error);
  });
};

export const trackThemeToggle = (theme: "light" | "dark"): void => {
  trackEvent({
    action: "toggle_theme",
    category: "UI",
    label: theme,
  });
};

export const trackSkillInteraction = (skillName: string): void => {
  trackEvent({
    action: "interact_skill",
    category: "Skills",
    label: skillName,
  });
};

// Advanced Custom Events

export const trackScrollDepth = (depth: number): void => {
  trackEvent({
    action: "scroll_depth",
    category: "Engagement",
    label: `${depth}%`,
    value: depth,
  });
};

export const trackTimeOnPage = (seconds: number): void => {
  trackEvent({
    action: "time_on_page",
    category: "Engagement",
    label: window.location.pathname,
    value: seconds,
  });
};

export const trackCTAClick = (ctaName: string, location: string): void => {
  trackEvent({
    action: "click_cta",
    category: "Conversion",
    label: `${ctaName} - ${location}`,
  });
};

export const trackNavigation = (from: string, to: string): void => {
  trackEvent({
    action: "navigate",
    category: "Navigation",
    label: `${from} → ${to}`,
  });
};

export const trackSearch = (query: string, resultsCount: number): void => {
  trackEvent({
    action: "search",
    category: "Search",
    label: query,
    value: resultsCount,
  });
};

export const trackFilter = (filterType: string, filterValue: string): void => {
  trackEvent({
    action: "apply_filter",
    category: "Filter",
    label: `${filterType}: ${filterValue}`,
  });
};

export const trackBlogRead = (postTitle: string, readPercentage: number): void => {
  trackEvent({
    action: "read_blog",
    category: "Blog",
    label: postTitle,
    value: readPercentage,
  });

  // Log to Firestore (simplified - just track that it was read)
  logFirestoreBlogRead(postTitle).catch((error) => {
    console.error("[Analytics] Failed to log blog read to Firestore:", error);
  });
};

export const trackCalendlyOpen = (): void => {
  trackEvent({
    action: "open_calendly",
    category: "Conversion",
    label: "Schedule Meeting",
  });
};

export const trackNewsletterSignup = (source: string): void => {
  trackEvent({
    action: "signup_newsletter",
    category: "Conversion",
    label: source,
  });
};

export const trackErrorEncountered = (errorType: string, errorMessage: string): void => {
  trackEvent({
    action: "error_encountered",
    category: "Error",
    label: `${errorType}: ${errorMessage}`,
  });
};

export const trackExternalLink = (url: string, linkText: string): void => {
  trackEvent({
    action: "click_external_link",
    category: "External",
    label: `${linkText} - ${url}`,
  });
};

export const trackVideoPlay = (videoTitle: string): void => {
  trackEvent({
    action: "play_video",
    category: "Media",
    label: videoTitle,
  });
};

export const trackFileDownload = (fileName: string, fileType: string): void => {
  trackEvent({
    action: "download_file",
    category: "Downloads",
    label: `${fileName} (${fileType})`,
  });
};

// Utility to track scroll depth automatically
let scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };

export const initScrollTracking = (): (() => void) | void => {
  if (typeof window === "undefined") return;

  const handleScroll = () => {
    const scrollPercentage = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    [25, 50, 75, 100].forEach((depth) => {
      if (scrollPercentage >= depth && !scrollDepthTracked[depth as keyof typeof scrollDepthTracked]) {
        scrollDepthTracked[depth as keyof typeof scrollDepthTracked] = true;
        trackScrollDepth(depth);
      }
    });
  };

  window.addEventListener("scroll", handleScroll, { passive: true });

  // Reset on navigation
  return () => {
    window.removeEventListener("scroll", handleScroll);
    scrollDepthTracked = { 25: false, 50: false, 75: false, 100: false };
  };
};

// Utility to track time on page
export const initTimeTracking = (): (() => void) | void => {
  if (typeof window === "undefined") return;

  const startTime = Date.now();
  const intervals = [30, 60, 120, 300]; // 30s, 1min, 2min, 5min
  const tracked: Record<number, boolean> = {};

  const checkInterval = setInterval(() => {
    const secondsOnPage = Math.floor((Date.now() - startTime) / 1000);

    intervals.forEach((interval) => {
      if (secondsOnPage >= interval && !tracked[interval]) {
        tracked[interval] = true;
        trackTimeOnPage(interval);
      }
    });

    // Stop tracking after 5 minutes
    if (secondsOnPage >= 300) {
      clearInterval(checkInterval);
    }
  }, 1000);

  // Cleanup on unmount
  return () => clearInterval(checkInterval);
};
